import { memo } from "react";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { deviceDisplayName, useRecentSimBatches } from "@/hooks/useSims";
import { shortIccid } from "@/lib/sims";

const KIND_LABELS = {
  activate: "Activate",
  deactivate: "Deactivate",
  label: "Label update",
} as const;

const STATUS_META = {
  queued: {
    label: "Queued",
    icon: Loader2,
    className: "animate-spin text-muted-foreground",
  },
  running: {
    label: "Running",
    icon: Loader2,
    className: "animate-spin text-muted-foreground",
  },
  completed: { label: "Done", icon: CheckCircle2, className: "text-success" },
  partially_failed: {
    label: "Partially failed",
    icon: AlertTriangle,
    className: "text-warning",
  },
  failed: { label: "Failed", icon: XCircle, className: "text-destructive" },
} as const;

/** Live progress of recent SIM action batches (pushed by Zero). */
export const SimBatchesPanel = memo(function SimBatchesPanel({
  limit = 8,
}: {
  limit?: number;
}) {
  const { data: batches, isLoading } = useRecentSimBatches(limit);

  return (
    <Card data-testid="sim-batches-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Recent actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && batches.length === 0 && (
          <p className="text-sm text-muted-foreground">No actions yet.</p>
        )}
        {batches.map((batch) => {
          const meta = STATUS_META[batch.status];
          const Icon = meta.icon;
          const summary = batch.summary ?? {};
          const problems = (batch.items ?? []).filter(
            (item) => item.state === "failed" || item.state === "unknown",
          );
          const device = deviceDisplayName(batch.instance);
          return (
            <div
              key={batch.id}
              className="rounded-md border p-3 text-sm"
              data-testid={`sim-batch-${batch.id}`}
              data-status={batch.status}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-medium">
                  <Icon className={`h-4 w-4 ${meta.className}`} />
                  {KIND_LABELS[batch.kind]}
                  {device && (
                    <span className="font-normal text-muted-foreground">
                      · {device}
                    </span>
                  )}
                </div>
                <Badge variant="outline" data-testid="sim-batch-status">
                  {meta.label}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {[
                  summary.completed ? `${summary.completed} done` : null,
                  summary.submitted ? `${summary.submitted} at carrier` : null,
                  summary.queued ? `${summary.queued} queued` : null,
                  summary.failed ? `${summary.failed} failed` : null,
                  summary.unknown ? `${summary.unknown} unconfirmed` : null,
                  summary.skipped ? `${summary.skipped} skipped` : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"}
                {batch.createdAt
                  ? ` · ${formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}`
                  : ""}
                {batch.requester
                  ? ` · ${batch.requester.firstName} ${batch.requester.lastName}`
                  : ""}
              </p>
              {problems.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-destructive">
                  {problems.slice(0, 5).map((item) => (
                    <li key={item.id}>
                      <span className="font-mono">
                        {item.simCard ? shortIccid(item.simCard.iccid) : "SIM"}
                      </span>
                      : {item.lastError ?? item.state}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
});
